/**
 * Load-tests media URLs and remembers the answers. A URL can be well formed
 * and still point at nothing, which only the browser can settle, so each is
 * tried once and listeners are told when one comes back dead: the grid marks
 * those cells, the editors put the previous media back.
 *
 * A probe that never answers counts as loadable — a slow network is not
 * evidence against a URL, and treating it as such would throw away media the
 * user picked deliberately.
 */
import { MEDIA_KIND } from '@/constants/media';
import { loadImageThrottled } from '@/utils/imageLoadQueue';
import type { MediaKind } from '@/types/library/gallery';

/** Long enough for a slow line, short enough not to hang a cell edit. */
const PROBE_TIMEOUT_MS = 10_000;

const verdicts = new Map<string, boolean>();
const inFlight = new Map<string, Promise<boolean>>();
const listeners = new Set<() => void>();

/** True only for a URL already tried and found unloadable — never a guess. */
export function isKnownUnloadableMedia(url: string): boolean {
  return verdicts.get(url.trim()) === false;
}

/** Notifies once per batch of verdicts, so the grid can redraw what changed. */
export function onMediaProbed(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Records what a rendered element already discovered, so nothing is loaded twice. */
export function recordMediaVerdict(url: string, loadable: boolean): void {
  const key = url.trim();
  const previous = verdicts.get(key);
  if (!key || previous === loadable) return;
  verdicts.set(key, loadable);
  // a cell flagged earlier has to clear once its media loads
  if (!loadable || previous === false) scheduleNotify();
}

/**
 * Loads the URL to find out whether it is real media. Never rejects.
 * `retryFailed` asks again about a URL that failed before, since one failed
 * load can be a passing blip and should not refuse it for the whole session.
 */
export function probeMediaUrl(
  url: string,
  kind: MediaKind,
  { retryFailed = false }: { retryFailed?: boolean } = {}
): Promise<boolean> {
  const key = url.trim();
  if (!key) return Promise.resolve(false);

  const known = verdicts.get(key);
  if (known === true || (known === false && !retryFailed)) {
    return Promise.resolve(known);
  }

  const pending = inFlight.get(key);
  if (pending) return pending;

  const probe = withTimeout(runProbe(key, kind)).then(loadable => {
    inFlight.delete(key);
    recordMediaVerdict(key, loadable);
    return loadable;
  });
  inFlight.set(key, probe);
  return probe;
}

/*
 * Helpers
 */

function runProbe(url: string, kind: MediaKind): Promise<boolean> {
  if (kind === MEDIA_KIND.Image) return loadImageThrottled(url);
  return probeElement(url, kind === MEDIA_KIND.Video ? 'video' : 'audio');
}

function probeElement(url: string, tag: 'video' | 'audio'): Promise<boolean> {
  return new Promise(resolve => {
    const el = document.createElement(tag);
    el.preload = 'metadata';

    let settled = false;
    const finish = (loadable: boolean) => {
      if (settled) return;
      settled = true;
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('error', onError);
      el.removeAttribute('src');
      el.load();
      resolve(loadable);
    };
    const onLoaded = () => finish(true);
    const onError = () => finish(false);

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('error', onError);
    el.src = url;
  });
}

function withTimeout(probe: Promise<boolean>): Promise<boolean> {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(true), PROBE_TIMEOUT_MS);
    void probe.then(loadable => {
      clearTimeout(timer);
      resolve(loadable);
    });
  });
}

let notifyQueued = false;

function scheduleNotify(): void {
  if (notifyQueued) return;
  notifyQueued = true;
  queueMicrotask(() => {
    notifyQueued = false;
    for (const listener of listeners) listener();
  });
}
